/**
 * Journey persistence — one key inside user_preferences.
 *
 * GET  /auth/me/preferences        → { preferences: { journey?: {...} } }
 * PATCH /auth/me/preferences       → { journey: {...} }   (merged per key)
 *
 * The backend PATCH merges per top-level key (see auth routes), so writing
 * `journey` can never clobber theme/language/streaming — the exact data-loss
 * bug Signal's BACKEND-SYNC.md reports in their preferences endpoint.
 */

import api from '@/services/api';

export interface JourneyPersisted {
  v: 1;
  neurons: number;
  /** Completed step ids, in completion order. */
  completed: string[];
  /** Step ids whose film was watched (so replay ≠ rewatch-forced). */
  watched: string[];
  skipped: boolean;
  startedAt: string | null;
  completedAt: string | null;
}

export const EMPTY_JOURNEY: JourneyPersisted = {
  v: 1,
  neurons: 0,
  completed: [],
  watched: [],
  skipped: false,
  startedAt: null,
  completedAt: null,
};

function normalize(raw: unknown): JourneyPersisted {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_JOURNEY };
  const r = raw as Partial<JourneyPersisted>;
  return {
    v: 1,
    neurons: typeof r.neurons === 'number' ? r.neurons : 0,
    completed: Array.isArray(r.completed) ? r.completed.filter((x) => typeof x === 'string') : [],
    watched: Array.isArray(r.watched) ? r.watched.filter((x) => typeof x === 'string') : [],
    skipped: r.skipped === true,
    startedAt: typeof r.startedAt === 'string' ? r.startedAt : null,
    completedAt: typeof r.completedAt === 'string' ? r.completedAt : null,
  };
}

export async function loadJourneyState(): Promise<JourneyPersisted> {
  const res = await api.get('/auth/me/preferences');
  return normalize(res.data?.preferences?.journey);
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pending: JourneyPersisted | null = null;

/**
 * Debounced save. Journey writes are frequent during a Try streak; one
 * request per ~800 ms pause is plenty, and the last write always wins.
 */
export function saveJourneyState(state: JourneyPersisted): void {
  pending = state;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    const body = pending;
    pending = null;
    saveTimer = null;
    if (body) void api.patch('/auth/me/preferences', { journey: body }).catch(() => undefined);
  }, 800);
}

/** Flush a pending save immediately (used before finale / unload). */
export function flushJourneyState(): void {
  if (saveTimer && pending) {
    clearTimeout(saveTimer);
    const body = pending;
    saveTimer = null;
    pending = null;
    void api.patch('/auth/me/preferences', { journey: body }).catch(() => undefined);
  }
}
